<template>
  <modal
    ref="modalComponent"
    modal-class="move-kr"
    title="Move KR to another Goal"
    :show="show"
    :hide-footer="true"
    size="lg"
    @hidden="$emit('input', false)"
    :static="true"
    :lazy="true"
  >
    <div class="text-normal">
      <highlighted-card>
        <div class="d-flex">
          <i class="kr-icon-greyed"></i>
          <div class="bold" v-no-capture>{{ kr.title }}</div>
        </div>
      </highlighted-card>
      <div class="target">
        <label class="d-block">First, choose where you want to move this to</label>
        <div class="d-flex">
          <org-selector-dropdown
            :selected="subOrg"
            @change="onSubOrgChange"
            :org-id="kr.organizationId"
            v-if="orgsCount > 1"
          />
          <org-team-dropdown
            class="ml-3"
            :parent-team-id="subOrg.id"
            :org-item="orgTarget"
            :show-org-group="true"
            :entity-context="ENTITY_TYPE.GOAL"
            @change="target = $event"
            :selected="target"
          />
          <timeline-filter-dropdown class="ml-3" v-model="selectedTimeline" />
        </div>
      </div>
      <div class="goals mt-2">
        <template v-if="goals">
          <div v-if="!isFetching && goals.length === 0" class="text-secondary text-small pl-2 ml-1">
            <span v-if="target.id">No team goals available for the selected filters</span>
            <span v-else>No Org goals available for the selected filters</span>
          </div>
          <div
            class="goal d-flex selectable position-relative"
            v-for="goal in goals"
            :key="goal.id"
          >
            <i class="goal-icon flex-shrink-0"></i>
            <span class="pl-2 goal-title text-normal bold" v-no-capture>{{ goal.title }}</span>
            <div class="move-to-goal position-absolute flex-center">
              <submit-buttons
                submit-text="Move to this Goal"
                :hide-cancel="true"
                :is-loading="movingToGoalId === goal.id"
                @save="moveToGoal(goal)"
              />
            </div>
          </div>
        </template>
      </div>
    </div>
  </modal>
</template>

<script>
import OrgSelectorDropdown from '@/components/goal/OrgSelectorDropdown'
import HighlightedCard from '@/components/HighlightedCard'
import SubmitButtons from '@/components/SubmitButtons'
import { ENTITY_TYPE } from '@/constants/entities'
import TeamMixin from '@/mixins/TeamMixin'
import showAPIError from '@/utils/showAPIError'
import * as R from 'ramda'
import Modal from '@/components/Modal'
import OrgTeamDropdown from '@/components/OrgTeamDropdown'
import TimelineFilterDropdown from '@/components/TimelineFilterDropdown'
import { intervalWithId } from '@/interval'
import { mapActions } from 'vuex'
import EventBus from '@/EventBus'
import { TOAST_TYPE } from '@/constants/toasts'
import { DEFAULT_ERROR } from '@/constants/messages'

/**
 * This component can be used to move kr to a different goal
 * Props:
 * - kr
 * - show (v-model)
 * @emits
 * - moved
 *    Emitted when kr to moved to another goal. Contains source and target information in payload
 * - input (v-model)
 */
export default {
  name: 'MoveKr',
  props: ['show', 'kr'],
  mixins: [TeamMixin],
  model: {
    prop: 'show'
  },
  components: {
    Modal,
    OrgTeamDropdown,
    TimelineFilterDropdown,
    SubmitButtons,
    HighlightedCard,
    OrgSelectorDropdown
  },
  data() {
    const subOrgId = this.getClosestOrgLikeTeamId(this.kr.teamId)
    const subOrg = this.$store.getters.teamById(subOrgId)
    return {
      target: {},
      goals: null,
      orgTarget: { name: 'Org Goals' },
      selectedTimeline: this.getKrTimeline(),
      isFetching: false,
      movingToGoalId: null,
      subOrgId,
      subOrg,
      ENTITY_TYPE,
      orgsCount: this.getOrgLikeTeamsOfOrg(this.kr.organizationId).length
    }
  },
  computed: {
    teams() {
      return this.$store.getters.userTeamsByOrgId(this.kr.organizationId)
    }
  },
  methods: {
    ...mapActions(['getGoals', 'moveKR']),
    getKrTimeline() {
      const goal = this.$store.getters.goalById(this.kr.goalId)
      return intervalWithId(goal.timeline)
    },
    async refreshGoals() {
      this.isFetching = true
      try {
        const goals = await this.getGoals({
          orgId: this.kr.organizationId,
          teamId: this.target.id || this.subOrg.id,
          options: {
            intervalStart: this.selectedTimeline.start,
            intervalEnd: this.selectedTimeline.end,
            intervalSubset: this.selectedTimeline.subset,
            isArchived: false,
            scopeId: this.subOrg.id
          }
        })
        this.goals = goals.filter(goal => goal.id !== this.kr.goalId)
      } catch (e) {
        showAPIError(e)
      }
      this.isFetching = false
    },
    async moveToGoal(goal) {
      try {
        this.movingToGoalId = goal.id
        await this.moveKR({
          krId: this.kr.id,
          goalId: this.kr.goalId,
          orgId: this.kr.organizationId,
          toGoalId: goal.id
        })
        this.$emit('moved', {
          fromGoalId: this.kr.goalId,
          toGoalId: goal.id
        })
        EventBus.$emit('ADD_TOAST', { message: 'KR has been moved successfully' })
      } catch (e) {
        EventBus.$emit('ADD_TOAST', {
          type: TOAST_TYPE.ERROR,
          message: R.pathOr(DEFAULT_ERROR, ['response', 'data', 'message'], e)
        })
      }
      this.movingToGoalId = null
    },
    onSubOrgChange(subOrg) {
      this.subOrg = subOrg
      this.target = this.orgTarget
    }
  },
  mounted() {
    const goal = this.$store.getters.goalById(this.kr.goalId)
    this.target = this.teams.find(R.propEq('id', goal.teamId))
    this.refreshGoals()
  },
  watch: {
    target: 'refreshGoals',
    subOrg: 'refreshGoals',
    selectedTimeline: 'refreshGoals'
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

.goals {
  min-height: 300px;
  padding-left: 12px;
  padding-top: 8px;
}
.target label {
  padding: 26px 12px 12px 18px;
  margin: 0;
}

.kr-icon-greyed {
  margin-right: 8px;
  margin-top: 4px;
  @include dimension(12px, 15px);
}
.goal {
  padding: 10px;
  border-radius: 10px;
  overflow: hidden;
}
.goal:hover {
  background-color: $selectionColor;
}
.move-to-goal {
  right: 0;
  top: 0;
  bottom: 0;
  background-color: $documentColor;
  font-weight: 600;
  display: none;
  padding: 0 12px 0 4px;
  /deep/ button {
    padding: 1px 12px;
  }
}
.goal:hover .move-to-goal {
  background-color: $selectionColor;
  display: flex;
}
.goal-icon {
  margin-top: 5px;
}
</style>
<style lang="scss">
.move-kr .modal-title {
  padding-left: 12px;
}
</style>
